import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendEmailVerification } from 'firebase/auth';
import { Helmet } from 'react-helmet-async';
import { Container, Box, Typography, Button, Alert, Paper, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

export default function VerifyEmailPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [resent, setResent] = useState(false);
  const [error, setError] = useState('');

  const handleResend = async () => {
    if (!auth.currentUser) return;
    try {
      await sendEmailVerification(auth.currentUser);
      setResent(true);
      setError('');
    } catch {
      setError('Could not resend email. Please wait a moment and try again.');
    }
  };

  const handleContinue = async () => {
    if (!auth.currentUser) return;
    await auth.currentUser.reload();
    if (auth.currentUser.emailVerified) {
      navigate('/dashboard');
    } else {
      setError('Your email has not been verified yet. Please check your inbox and click the link.');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Helmet>
        <title>Verify Your Email - BrainLoop</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Container maxWidth="xs">
        <Paper
          elevation={8}
          sx={{
            p: 4,
            borderRadius: 2,
            textAlign: 'center',
            background: theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.default, 0.9)
              : alpha(theme.palette.background.paper, 0.9),
            backdropFilter: 'blur(10px)',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <MarkEmailReadIcon sx={{ fontSize: 56, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Check your inbox
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            We sent a verification link to <strong>{user?.email}</strong>. Click it to activate your account.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {resent && <Alert severity="success" sx={{ mb: 2 }}>Verification email resent.</Alert>}

          <Button variant="contained" fullWidth onClick={handleContinue} sx={{ mb: 1.5 }}>
            I've verified my email
          </Button>
          <Button variant="outlined" fullWidth onClick={handleResend} disabled={resent} sx={{ mb: 1.5 }}>
            Resend email
          </Button>
          <Button variant="text" fullWidth onClick={handleLogout} color="inherit">
            Sign out
          </Button>
        </Paper>
      </Container>
    </Box>
  );
}
